import React, { useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  renderRoute,
  routeAccount,
  routeAdminPlan,
  routeAdminUsers,
  routeClients,
  routeContextPreviewConsole,
  routeDocumentCreation,
  routeDocumentSelection,
  routeFeedback,
  routeLogin,
  routePageCounter,
  routeStatementClassifier,
} from "@navigation/typedRoutes";
import { useAccountClient } from "@contexts/AccountClient.context";
import { useAuthenticationClient } from "@contexts/AuthenticationClient.context";
import { useJwtController } from "@contexts/Jwt.context";
import {
  FileText,
  CreditCard,
  Eye,
  PlusCircle,
  Shield,
  UserCog,
  Users,
} from "lucide-react";
import { useIoState } from "@utils/useIoState";
import { getPendingEmailValidation } from "@ps-bridge/services/pendingEmailValidation";
import { ioEffect, taskEitherFold, taskEitherFromEffect, taskFromIO, taskOf } from "@utils/effects/branded";
import { runTaskEither } from "@view/effects/effectRunner";
import * as IO from "@ps-bridge/interop/io";
import { ok } from "@ps-bridge/ok";
import type { AuthenticationError } from "@ps-bridge/models/core/dataContracts";
import type { IOts, OptionTs, TaskTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";
import * as Option from "@utils/Option";
import { cleanupNone, cleanupSome, useIoEffectWithCleanup } from "@hooks/effects/useIoEffectWithCleanup";
import { useTaskOnInit } from "@hooks/effects/useTaskEffect";
import { actionEffect } from "@ps-bridge/jwtSession.program";
import WorkspaceSidebarPanel, {
  type WorkspaceMenuPhase,
  type WorkspaceNavItem,
  type WorkspaceNavLayoutPhase,
  type WorkspaceNavLinkPhase,
  type WorkspaceNavSubNavPhase,
} from "@view/panels/workspace/WorkspaceSidebar.panel";

const routePaths = {
  account: renderRoute(routeAccount),
  adminPlan: renderRoute(routeAdminPlan),
  adminUsers: renderRoute(routeAdminUsers),
  clients: renderRoute(routeClients),
  documentCreation: renderRoute(routeDocumentCreation),
  documentSelection: renderRoute(routeDocumentSelection),
  feedback: renderRoute(routeFeedback),
  contextPreviewConsole: renderRoute(routeContextPreviewConsole),
  pageCounter: renderRoute(routePageCounter),
  statementClassifier: renderRoute(routeStatementClassifier),
  login: renderRoute(routeLogin),
};

const toNavIcon =
  (Icon: React.ComponentType<{ size: number | string }>) =>
  ({ size }: { size: number }) =>
    <Icon size={size} />;

const nonEmptyString = (value: string): OptionTs<string> => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? Option.some(trimmed) : Option.none<string>();
};

const documentChildren = [
  {
    label: "Create",
    to: routePaths.documentCreation,
    icon: toNavIcon(PlusCircle as React.ComponentType<{ size: number | string }>),
    featureGate: "always" as const,
  },
];

const toolsChildren = [
  {
    label: "Context Preview",
    to: routePaths.contextPreviewConsole,
    icon: toNavIcon(Eye as React.ComponentType<{ size: number | string }>),
    featureGate: "always" as const,
  },
  {
    label: "Statement Classifier",
    to: routePaths.statementClassifier,
    icon: toNavIcon(FileText as React.ComponentType<{ size: number | string }>),
    featureGate: "statementClassifier" as const,
  },
];

const adminChildren = [
  {
    label: "Users",
    to: routePaths.adminUsers,
    icon: toNavIcon(UserCog as React.ComponentType<{ size: number | string }>),
    featureGate: "always" as const,
  },
  {
    label: "Manage Plan",
    to: routePaths.adminPlan,
    icon: toNavIcon(CreditCard as React.ComponentType<{ size: number | string }>),
    featureGate: "always" as const,
  },
];

const navItems = [
  {
    label: "Client Workspace",
    to: routePaths.clients,
    icon: toNavIcon(Users as React.ComponentType<{ size: number | string }>),
  },
  {
    label: "Document Studio",
    to: routePaths.documentSelection,
    icon: toNavIcon(FileText as React.ComponentType<{ size: number | string }>),
    children: documentChildren,
  },
  {
    label: "Secure Tools",
    to: routePaths.contextPreviewConsole,
    icon: toNavIcon(FileText as React.ComponentType<{ size: number | string }>),
    children: toolsChildren,
  },
  {
    label: "Administration",
    to: routePaths.adminUsers,
    icon: toNavIcon(Shield as React.ComponentType<{ size: number | string }>),
    children: adminChildren,
  },
];

const WorkspaceSidebar: React.FC = () => {
  const accountClient = useAccountClient();
  const authenticationClient = useAuthenticationClient();
  const { clearSession } = useJwtController();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingEmailValidation = getPendingEmailValidation();
  const { read: accountName, set: setAccountName } = useIoState("Account");
  const { read: organizationName, set: setOrganizationName } = useIoState("Organization");
  const [fullName, setFullName] = useState("Account");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuPhase: WorkspaceMenuPhase = menuOpen ? "open" : "closed";

  const onInitTask = useMemo<OptionTs<TaskTs<Ok>>>(
    () => {
      if (location.pathname === routePaths.account) {
        return Option.none<TaskTs<Ok>>();
      }

      return Option.some(
        taskEitherFold(
          accountClient.getAccountDetails,
          () => taskOf(ok),
          (response) => {
            const firstName = Option.fold(Option.map(String)(response.firstName), () => "", (value) => value);
            const lastName = Option.fold(Option.map(String)(response.lastName), () => "", (value) => value);
            const nameParts = [firstName, lastName].filter((value) => value.length > 0);
            const emailValue = nonEmptyString(String(response.email));
            const displayName = nameParts.length > 0
              ? nameParts.join(" ")
              : Option.fold(emailValue, () => "Account", (value) => value);
            const orgName = Option.fold(
              Option.map(String)(response.organizationName),
              () => "Organization",
              (value) => Option.fold(nonEmptyString(value), () => "Organization", (name) => name),
            );
            return taskFromIO(
              IO.sequenceOk([
                ioEffect(() => {
                  setFullName(displayName);
                  return ok;
                }),
                setAccountName(displayName),
                setOrganizationName(orgName),
              ])
            );
          },
        ),
      );
    },
    [accountClient, location.pathname, setAccountName, setOrganizationName],
  );

  useTaskOnInit(onInitTask);

  const menuListenerSetup = useMemo(
    () =>
      ioEffect(() => {
        if (!menuOpen) {
          return cleanupNone();
        }
        const handler = () => setMenuOpen(false);
        window.addEventListener("click", handler);
        return cleanupSome(ioEffect(() => {
          window.removeEventListener("click", handler);
          return ok;
        }));
      }),
    [menuOpen],
  );

  useIoEffectWithCleanup(menuListenerSetup);

  const handleLogout = useCallback(async () => {
    setMenuOpen(false);
    await runTaskEither(
      taskEitherFromEffect(
        authenticationClient.logout,
        (reason) => reason as AuthenticationError,
      ),
      {
        onLeft: () => IO.pure(ok),
        onRight: () => IO.pure(ok),
      },
    );
    IO.run(actionEffect(clearSession));
    navigate(routePaths.login);
  }, [authenticationClient, clearSession, navigate]);

  const isAllowedPath = useCallback(
    (path: string) => !pendingEmailValidation || path === routePaths.adminUsers,
    [pendingEmailValidation]
  );

  const navViewModels: WorkspaceNavItem[] = useMemo(
    () =>
      navItems.map(({ label, to, icon, children }) => {
        const parentAllowed = isAllowedPath(to);
        const filteredChildren =
          children
            ?.filter((child) => isAllowedPath(child.to))
            .map((child) => ({
              key: child.label,
              label: child.label,
              to: child.to,
              icon: child.icon,
              featureGate: child.featureGate,
            })) ?? [];
        const hasEnabledChild = filteredChildren.length > 0;
        const hidden = !parentAllowed && !hasEnabledChild;
        const linkPhase: WorkspaceNavLinkPhase = hidden
          ? "hidden"
          : parentAllowed
          ? "enabled"
          : "disabled";
        const layoutPhase: WorkspaceNavLayoutPhase = hidden
          ? "hidden"
          : children
          ? "group"
          : "single";
        const subNavPhase: WorkspaceNavSubNavPhase =
          layoutPhase === "group" && hasEnabledChild ? "visible" : "hidden";

        return {
          key: label,
          label,
          to,
          icon,
          linkPhase,
          layoutPhase,
          subNavPhase,
          children: filteredChildren,
        };
      }),
    [isAllowedPath],
  );

  const handleMenuToggle = useMemo(
    () =>
      ioEffect(() => {
        setMenuOpen((prev) => !prev);
        return ok;
      }),
    [],
  );

  const handleAccountSettings = useMemo(
    () =>
      ioEffect(() => {
        setMenuOpen(false);
        navigate(routePaths.account);
        return ok;
      }),
    [navigate],
  );

  const handleFeedback = useMemo(
    () =>
      ioEffect(() => {
        setMenuOpen(false);
        navigate(routePaths.feedback);
        return ok;
      }),
    [navigate],
  );

  const handleLogoutEffect = useMemo(
    () =>
      ioEffect(() => {
        handleLogout()
          .then(() => ok)
          .catch(() => ok);
        return ok;
      }),
    [handleLogout],
  );

  const runPanelIO = useCallback((action: IOts<Ok>): Ok => IO.run(action), []);

  return (
    <WorkspaceSidebarPanel
      accountName={accountName}
      organizationName={organizationName}
      fullName={fullName}
      navItems={navViewModels}
      pageCounterPath={routePaths.pageCounter}
      menuPhase={menuPhase}
      runIO={runPanelIO}
      onMenuToggle={handleMenuToggle}
      onAccountSettings={handleAccountSettings}
      onFeedback={handleFeedback}
      onLogout={handleLogoutEffect}
    />
  );
};

export default WorkspaceSidebar;
